/* Copyright (c) 2025 MOCA Systems, Inc. All rights reserved. */
import { Injectable, inject } from '@angular/core';
import { TicketScale } from '@touchplan/common';
import { DynamicFlagTypes } from '@touchplan/feature-flags';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { FeatureFlagManager } from 'src/app/services/FeatureFlagManager';

@Injectable({
	providedIn: 'root',
})
export class GranularEnabledService {
	private featureFlagManager = inject(FeatureFlagManager);

	private projectId$ = new BehaviorSubject<string | undefined>(undefined);
	private granularScaleSubject$ = new BehaviorSubject<TicketScale>(TicketScale.Daily);
	private flagGloballyEnabled$: Observable<boolean> = this.featureFlagManager.getFlag$(
		DynamicFlagTypes.FBDB_GTP_ENABLED
	);
	private flagByProject$: Observable<boolean | undefined> = this.projectId$.pipe(
		switchMap((projectId) => {
			if (!projectId) {
				return of(undefined);
			}
			return this.featureFlagManager.getFlag$(DynamicFlagTypes.FBDB_GTP_ENABLED_BY_PROJECT, projectId);
		})
	);

	/**
	 * combined check that validates both feature flags
	 * doesn't align with fbdb in that we can't enable globally and disable by project
	 */
	public isGranularEnabled$ = combineLatest([this.flagGloballyEnabled$, this.flagByProject$]).pipe(
		map(([global, project]) => project || global)
		// alternative version that mirrors fbdb, but that our feature flag system... "struggles" to support
		//map(([global, project]) => project !== undefined ? project : global )
	);

	/** Emit the current global rendering scale, always emits daily while the feature flag is off */
	public granularScale$ = combineLatest([this.granularScaleSubject$, this.isGranularEnabled$]).pipe(
		map(([scale, isEnabled]) => (isEnabled ? scale : TicketScale.Daily))
	);

	private _isGranularEnabled: boolean = false;
	/** prefer observable version **/
	public get isGranularEnabledSync(): boolean {
		return this._isGranularEnabled;
	}

	private _granularScale: TicketScale = TicketScale.Daily;
	/** prefer observable version **/
	public get granularScaleSync(): TicketScale {
		return this._granularScale;
	}

	/**
	 * to be maintained by... a state control component like plan.component
	 * ideally we'd have this is a more global state service
	 * */
	public setProjectId(projectId: string): void {
		this.projectId$.next(projectId);
	}
	/** to be maintained by... a state control component like plan.component */
	public clearProjectId(): void {
		this.projectId$.next(undefined);
	}

	public setRenderingMode(granularScale: TicketScale) {
		this.granularScaleSubject$.next(granularScale);
	}

	constructor() {
		this.isGranularEnabled$.subscribe((isEnabled) => {
			this._isGranularEnabled = isEnabled;
		});
		this.granularScale$.subscribe((scale) => {
			this._granularScale = scale;
		});
	}
}
